import { useApolloClient } from '@apollo/client';
import {
  type ActionHandler,
  Create,
  type Nullable,
  type ObjectSchemaDefinition,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { Field, type FormikValues } from 'formik';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  type CreateServiceAccountMutation,
  type CreateServiceAccountMutationVariables,
  useCreateServiceAccountMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';

const serviceAccountCreateSchema = Yup.object<ObjectSchemaDefinition>({
  name: Yup.string().required('Name is a required field').max(100),
});

type FormData = Nullable<
  CreateServiceAccountMutationVariables['input']['serviceAccount']
>;

type SubmitResponse = Nullable<
  CreateServiceAccountMutation['createServiceAccount']
>;

export const ServiceAccountCreate: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const apolloClient = useApolloClient();
  const [serviceAccountCreate] = useCreateServiceAccountMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormikValues): Promise<SubmitResponse> => {
      const response = await serviceAccountCreate({
        variables: {
          input: {
            serviceAccount: {
              name: formData.name,
              environmentId: envId,
            },
          },
        },
      });
      return response.data?.createServiceAccount ?? null;
    },
    [serviceAccountCreate, envId],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      const serviceAccount = submitResponse?.serviceAccount;
      if (serviceAccount) {
        history.push(
          `/env/${envId}/serviceAccounts/${serviceAccount.id}/clientSecret`,
        );
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [history, envId],
  );

  return (
    <Create<FormData, SubmitResponse>
      title="New Service Account"
      validationSchema={serviceAccountCreateSchema}
      saveData={saveData}
      onProceed={onProceed}
      cancelNavigationUrl={`/env/${envId}/serviceAccounts`}
      initialData={{
        loading: false,
      }}
    >
      <Field name="name" label="Name" as={SingleLineTextField} />
    </Create>
  );
};

export const ServiceAccountCreateCrumb: BreadcrumbResolver = () =>
  'New Service Account';
